import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Archive } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useSetoresManejo, useUpdateFertirrigavel,
  useProdutosManejo, useCreateProdutoManejo, useUpdateProdutoManejo,
  useProgramasPorSetor, useCreatePrograma, useUpdateProgramaStatus,
} from "@/hooks/use-manejo";
import type { MnjProduto, MnjProdutoUnidade } from "@shared/schema";
import { MNJ_PRODUTO_UNIDADE } from "@shared/schema";

import { PageHeader } from "@/components/layout/page-header";
import { PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
import { PageTabs } from "@/components/layout/page-tabs";
import { PageToolbar } from "@/components/layout/page-toolbar";
// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook). As duas abas com
// busca compartilham a chave `q`: só uma fica montada por vez (Radix Tabs
// desmonta o conteúdo inativo), e a mesma coisa tem o mesmo nome no repo.
const PADRAO_BUSCA = { q: "" };
const PADRAO_PROGRAMA_TAB = { setor: "" };
const PADRAO_PROGRAMA = { aba: "programa" };
const PERMITIDOS_PROGRAMA = { aba: ["programa", "setores", "produtos"] } as const;

// ─── Setores (Sprint 1: setores é o canônico — ver Rizoma_Arquitetura_Sync_Aegro.md §4.2) ──
// Nome, área, data de plantio etc. são editados no cadastro de Setores do Cacho —
// aqui só lista e alterna `fertirrigavel`, o único campo que o Manejo escreve.

function SetoresTab() {
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const { toast } = useToast();
  const { data: setores = [], isLoading } = useSetoresManejo(true);
  const update = useUpdateFertirrigavel();

  const handleToggle = async (id: string, fertirrigavel: boolean) => {
    try {
      await update.mutateAsync({ id, fertirrigavel });
    } catch (e: any) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  };

  if (isLoading) return <PageSkeleton variante="tabela" linhas={6} />;

  const visiveis = setores.filter(s => combina(busca, s.nome));

  return (
    <div className="space-y-3">
      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Setor" testId="input-busca-setores-manejo" />}
      >
        <p className="text-xs text-muted-foreground">
          Nome, área e data de plantio são editados no cadastro de Setores (Cacho). Aqui só se define quais setores recebem fertirrigação.
        </p>
      </PageToolbar>

      <DataCard meta={`${visiveis.length} setor(es)`}>
        {visiveis.length === 0 ? (
          busca.trim()
            ? <NadaEncontrado termo={busca} />
            : <EmptyState titulo="Nenhum setor cadastrado" descricao="Os setores vêm do cadastro do Cacho — crie-os lá e eles aparecem aqui." />
        ) : (
          <DataTable colunas={[null, "9rem", "8rem", "9rem", "8rem"]}>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead>Nome</DataTableHead>
                <DataTableHead>Plantio</DataTableHead>
                <DataTableHead num>Área (ha)</DataTableHead>
                <DataTableHead>Fertirrigável</DataTableHead>
                <DataTableHead>Status</DataTableHead>
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {visiveis.map(s => (
                <DataTableRow key={s.id}>
                  {/* `atenuado` só no texto: o Switch é justamente o controle usado
                      para reativar o registro e não pode desbotar junto. */}
                  <DataTableCell destaque className={!s.ativo ? "opacity-50" : ""}>{s.nome}</DataTableCell>
                  <DataTableCell className={!s.ativo ? "opacity-50" : ""}>{s.dataPlantio ?? "—"}</DataTableCell>
                  <DataTableNum className={!s.ativo ? "opacity-50" : ""}>{s.areaHectares ? Number(s.areaHectares).toFixed(2) : "—"}</DataTableNum>
                  <DataTableCell>
                    <Switch checked={s.fertirrigavel} onCheckedChange={v => handleToggle(s.id, v)} disabled={update.isPending} />
                  </DataTableCell>
                  <DataTableCell>
                    {s.ativo ? <StatusBadge tom="sucesso" ponto>ativo</StatusBadge> : <StatusBadge tom="neutro">inativo</StatusBadge>}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </DataCard>
    </div>
  );
}

// ─── Produtos ─────────────────────────────────────────────────────────────────

function ProdutosTab() {
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const { toast } = useToast();
  const { data: produtos = [], isLoading } = useProdutosManejo(true);
  const create = useCreateProdutoManejo();
  const update = useUpdateProdutoManejo();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MnjProduto | null>(null);
  const [form, setForm] = useState<{ nome: string; aliases: string; unidade: MnjProdutoUnidade; ativo: boolean }>(
    { nome: "", aliases: "", unidade: "kg", ativo: true },
  );

  const openCreate = () => { setEditing(null); setForm({ nome: "", aliases: "", unidade: "kg", ativo: true }); setOpen(true); };
  const openEdit = (p: MnjProduto) => {
    setEditing(p);
    setForm({ nome: p.nome, aliases: p.aliases.join(", "), unidade: p.unidade, ativo: p.ativo });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const aliases = form.aliases.split(",").map(a => a.trim()).filter(Boolean);
      const payload = { nome: form.nome.trim(), aliases, unidade: form.unidade, ativo: form.ativo };
      if (editing) await update.mutateAsync({ id: editing.id, data: payload });
      else await create.mutateAsync(payload);
      toast({ title: editing ? "Produto atualizado!" : "Produto criado!" });
      setOpen(false);
    } catch (e: any) {
      toast({ title: e.message?.includes("409") ? "Nome já cadastrado" : "Erro ao salvar", variant: "destructive" });
    }
  };

  if (isLoading) return <PageSkeleton variante="tabela" linhas={6} />;

  const visiveis = produtos.filter(p => combina(busca, p.nome, p.aliases.join(" ")));

  return (
    <div className="space-y-3">
      <PageToolbar
        fim={
          <>
            <PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Produto" testId="input-busca-produtos-manejo" />
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" />Novo produto</Button>
          </>
        }
      >
        <span />
      </PageToolbar>

      <DataCard meta={`${visiveis.length} produto(s)`}>
        {visiveis.length === 0 ? (
          busca.trim()
            ? <NadaEncontrado termo={busca} />
            : <EmptyState
                titulo="Nenhum produto cadastrado"
                descricao="O produto é o que o parser reconhece no texto do programa — cadastre os aliases junto."
                acao={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" />Novo produto</Button>}
              />
        ) : (
          <DataTable colunas={[null, "16rem", "7rem", "8rem", "4rem"]}>
            <DataTableHeader>
              <DataTableRow>
                <DataTableHead>Nome</DataTableHead>
                <DataTableHead>Aliases</DataTableHead>
                <DataTableHead>Unidade</DataTableHead>
                <DataTableHead>Status</DataTableHead>
                <DataTableHead />
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {visiveis.map(p => (
                <DataTableRow key={p.id}>
                  <DataTableCell destaque className={!p.ativo ? "opacity-50" : ""}>{p.nome}</DataTableCell>
                  <DataTableCell className={`text-xs text-muted-foreground truncate ${!p.ativo ? "opacity-50" : ""}`}>{p.aliases.join(", ") || "—"}</DataTableCell>
                  <DataTableCell className={!p.ativo ? "opacity-50" : ""}>{p.unidade}</DataTableCell>
                  <DataTableCell>
                    {p.ativo ? <StatusBadge tom="sucesso" ponto>ativo</StatusBadge> : <StatusBadge tom="neutro">inativo</StatusBadge>}
                  </DataTableCell>
                  <DataTableCell><Button variant="ghost" size="icon" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button></DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </DataCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome</Label><Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Sulfato de Amônio" /></div>
            <div className="space-y-1">
              <Label>Aliases <span className="text-muted-foreground font-normal">(separados por vírgula)</span></Label>
              <Input value={form.aliases} onChange={e => setForm(f => ({ ...f, aliases: e.target.value }))} placeholder="SA, sulfato de amonio" />
            </div>
            {/* Lista fechada, não campo livre: a unidade passou a ter CHECK no banco
                (migração 052) porque é NELA que a quantidade da operação é lida —
                um "litro" digitado à mão viraria erro de constraint no salvar. */}
            <div className="space-y-1">
              <Label>Unidade</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={form.unidade}
                onChange={e => setForm(f => ({ ...f, unidade: e.target.value as MnjProdutoUnidade }))}
              >
                {MNJ_PRODUTO_UNIDADE.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between"><Label>Ativo</Label><Switch checked={form.ativo} onCheckedChange={v => setForm(f => ({ ...f, ativo: v }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={!form.nome.trim()}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Programa (masterplan) ────────────────────────────────────────────────────

interface ItemForm { mnjProdutoId: string; kgPorHa: string }

function ProgramaTab() {
  const { toast } = useToast();
  const { data: setores = [] } = useSetoresManejo();
  const { data: produtos = [] } = useProdutosManejo();
  const { estado: filtrosPrograma, definir: definirPrograma } = useEstadoNaUrl(PADRAO_PROGRAMA_TAB);
  const setorId = filtrosPrograma.setor;
  const { data: programas = [], isLoading } = useProgramasPorSetor(setorId || null);
  const create = useCreatePrograma();
  const arquivar = useUpdateProgramaStatus();

  const [open, setOpen] = useState(false);
  const [nome, setNome] = useState("");
  const [itens, setItens] = useState<ItemForm[]>([{ mnjProdutoId: "", kgPorHa: "" }]);

  const openCreate = () => { setNome(""); setItens([{ mnjProdutoId: "", kgPorHa: "" }]); setOpen(true); };
  const updateItem = (idx: number, patch: Partial<ItemForm>) => setItens(its => its.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const addItem = () => setItens(its => [...its, { mnjProdutoId: "", kgPorHa: "" }]);
  const removeItem = (idx: number) => setItens(its => its.length > 1 ? its.filter((_, i) => i !== idx) : its);

  const handleSave = async () => {
    if (!setorId) return;
    const validos = itens.filter(it => it.mnjProdutoId && it.kgPorHa);
    if (validos.length === 0) return;
    try {
      await create.mutateAsync({ setorId, nome: nome.trim() || "Programa", itens: validos });
      toast({ title: "Programa criado!" });
      setOpen(false);
    } catch (e: any) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  };

  const handleArquivar = async (id: string) => {
    await arquivar.mutateAsync({ id, status: "arquivado" });
    toast({ title: "Programa arquivado." });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <select className="h-9 rounded-md border border-input bg-background px-3 text-sm flex-1 max-w-xs" value={setorId} onChange={e => definirPrograma({ setor: e.target.value })}>
          <option value="">Selecione um setor...</option>
          {setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <Button size="sm" onClick={openCreate} disabled={!setorId}><Plus className="h-4 w-4 mr-1" />Novo programa</Button>
      </div>

      {!setorId ? (
        <EmptyState
          titulo="Selecione um setor"
          descricao="O masterplan é por setor — escolha um acima para ver os programas dele."
          compacto
        />
      ) : isLoading ? (
        <PageSkeleton variante="cards" linhas={3} />
      ) : programas.length === 0 ? (
        <EmptyState
          titulo="Nenhum programa neste setor"
          descricao="O programa é a dose por hectare que a operação usa como base — crie o primeiro."
          acao={<Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" />Novo programa</Button>}
        />
      ) : (
        <div className="space-y-2">
          {programas.map(p => (
            <DataCard
              key={p.id}
              titulo={p.nome}
              estado={
                p.status === "ativo"
                  ? <StatusBadge tom="sucesso" ponto>ativo</StatusBadge>
                  : <StatusBadge tom="neutro">{p.status}</StatusBadge>
              }
              acao={
                p.status === "ativo" ? (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleArquivar(p.id)}>
                    <Archive className="h-3.5 w-3.5" />
                  </Button>
                ) : undefined
              }
              padded
            >
              <div className="text-xs text-muted-foreground space-y-0.5">
                {p.itens.map(it => (
                  <p key={it.id}>
                    {it.produto.nome}: <span className="font-mono tabular-nums">{Number(it.kgPorHa).toFixed(2)}</span> kg/ha
                  </p>
                ))}
              </div>
            </DataCard>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo programa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome</Label><Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Programa 2026" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Itens (alvo kg/ha)</Label>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" />Item</Button>
              </div>
              {itens.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm" value={it.mnjProdutoId} onChange={e => updateItem(idx, { mnjProdutoId: e.target.value })}>
                    <option value="">Produto...</option>
                    {produtos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                  <Input type="number" step="0.0001" className="w-28" placeholder="kg/ha" value={it.kgPorHa} onChange={e => updateItem(idx, { kgPorHa: e.target.value })} />
                  <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} disabled={itens.length === 1}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ABAS = [
  { id: "programa", label: "Programa" },
  { id: "setores", label: "Setores" },
  { id: "produtos", label: "Produtos" },
];

export default function ManejoPrograma() {
  const { estado, definir } = useEstadoNaUrl(PADRAO_PROGRAMA, PERMITIDOS_PROGRAMA);
  const aba = estado.aba;
  const setAba = (v: string) => definir({ aba: v });

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader titulo="Programa" descricao="Masterplan, setores e catálogo de produtos" />

      {/* Sub-navegação: troca de PÁGINA dentro da página. O Radix Tabs continua
          sendo o comutador de conteúdo — só a TabsList some, virou PageTabs. */}
      <PageTabs abas={ABAS} ativa={aba} onChange={setAba} />

      <Tabs value={aba} onValueChange={setAba}>
        <TabsContent value="programa"><ProgramaTab /></TabsContent>
        <TabsContent value="setores"><SetoresTab /></TabsContent>
        <TabsContent value="produtos"><ProdutosTab /></TabsContent>
      </Tabs>
    </div>
  );
}
